import { SelectInput, ISelectOpt } from 'app/components/common/Select';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router';

interface BasePickerProps {
	queryParam?: string;
}

// all date pickers list the most recent BM/KW first, regardless of the order the store provides
const sortOptsNewestFirst = (opts: ISelectOpt[]): ISelectOpt[] => {
	return opts.slice().sort((a, b) => Number(b.value) - Number(a.value));
};

interface MonthPickerProps extends BasePickerProps {
	value?: number;
	opts: ISelectOpt[];
	onChange: (value: number) => void;
	emptyText?: string;
}

interface RangePickerProps extends BasePickerProps {
	minValue?: number;
	maxValue?: number;
	opts: ISelectOpt[];
	onMinChange: (value: number) => void;
	onMaxChange: (value: number) => void;
	emptyTextMin?: string;
	emptyTextMax?: string;
	rangeLabel?: string;
}

export const MonthPicker = observer((props: MonthPickerProps) => {
	const location = useLocation();
	const history = useHistory();

	const { value, opts, onChange, emptyText = 'Berichtsmonat', queryParam = 'bm' } = props;

	// Initialize from query params on mount
	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const paramValue = urlParams.get(queryParam);

		if (paramValue && !value) {
			const numValue = parseInt(paramValue);
			if (!isNaN(numValue)) {
				onChange(numValue);
			}
		}
	}, []);

	// Update query params when value changes
	useEffect(() => {
		if (value !== undefined && value !== null) {
			const urlParams = new URLSearchParams(location.search);
			urlParams.set(queryParam, value.toString());

			const newSearch = urlParams.toString();
			const newUrl = location.pathname + '?' + newSearch;

			if (location.pathname + location.search !== newUrl) {
				history.replace(newUrl);
			}
		}
	}, [value, queryParam, location, history]);

	const handleChange = (name: string, opt: any) => {
		const newValue = parseInt(opt.value);
		onChange(newValue);
	};

	return (
		<div className={'grid is-col-auto align-center justify-start pad-1rem'}>
			<strong>Monat: </strong>
			<div>
				<SelectInput name="monthPicker" emptyText={emptyText} value={value} onChange={handleChange} opts={sortOptsNewestFirst(opts)} isClearable={false} />
			</div>
		</div>
	);
});

export const MonthRangePicker = observer((props: RangePickerProps) => {
	const location = useLocation();
	const history = useHistory();
	const isInitializing = useRef(true);

	const { minValue, maxValue, opts, onMinChange, onMaxChange, emptyTextMin = 'Start BM', emptyTextMax = 'End BM', rangeLabel = 'Monat', queryParam = 'bm' } = props;

	// Initialize from query params on mount - this runs FIRST
	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const minParam = urlParams.get(`${queryParam}Min`);
		const maxParam = urlParams.get(`${queryParam}Max`);

		// Always initialize from query params if they exist
		// This ensures query params take precedence on page refresh
		if (minParam) {
			const numValue = parseInt(minParam);
			if (!isNaN(numValue)) {
				onMinChange(numValue);
			}
		}

		if (maxParam) {
			const numValue = parseInt(maxParam);
			if (!isNaN(numValue)) {
				onMaxChange(numValue);
			}
		}

		// Mark initialization as complete after a small delay to allow store to settle
		setTimeout(() => {
			isInitializing.current = false;
		}, 100);
	}, []);

	// Update query params when values change (but not during initialization)
	useEffect(() => {
		// Skip URL updates during initial mount to avoid overwriting query params
		if (isInitializing.current) {
			return;
		}

		const urlParams = new URLSearchParams(location.search);

		if (minValue !== undefined && minValue !== null) {
			urlParams.set(`${queryParam}Min`, minValue.toString());
		} else {
			urlParams.delete(`${queryParam}Min`);
		}

		if (maxValue !== undefined && maxValue !== null) {
			urlParams.set(`${queryParam}Max`, maxValue.toString());
		} else {
			urlParams.delete(`${queryParam}Max`);
		}

		const newSearch = urlParams.toString();
		const newUrl = location.pathname + (newSearch ? '?' + newSearch : '');

		if (location.pathname + location.search !== newUrl) {
			history.replace(newUrl);
		}
	}, [minValue, maxValue, queryParam, location, history]);

	const handleMinChange = (name: string, opt: any) => {
		const newValue = parseInt(opt.value);
		onMinChange(newValue);
	};

	const handleMaxChange = (name: string, opt: any) => {
		const newValue = parseInt(opt.value);
		onMaxChange(newValue);
	};

	return (
		<div className={'grid is-col-auto align-center justify-start pad-1rem zeitraum-select'}>
			<strong>{rangeLabel}: </strong>
			<div>
				<SelectInput name="monthRangeMin" emptyText={emptyTextMin} value={minValue} onChange={handleMinChange} opts={sortOptsNewestFirst(opts)} isClearable={false} />
			</div>
			<span>bis</span>
			<div>
				<SelectInput name="monthRangeMax" emptyText={emptyTextMax} value={maxValue} onChange={handleMaxChange} opts={sortOptsNewestFirst(opts)} isClearable={false} />
			</div>
		</div>
	);
});

export const WeekPicker = observer((props: MonthPickerProps) => {
	const location = useLocation();
	const history = useHistory();

	const { value, opts, onChange, emptyText = 'Kalenderwoche', queryParam = 'kw' } = props;

	// Initialize from query params on mount, or set to max KW if no query param
	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const paramValue = urlParams.get(queryParam);

		if (paramValue) {
			const numValue = parseInt(paramValue);
			if (!isNaN(numValue) && !value) {
				onChange(numValue);
			}
		} else if (!value && opts.length > 0) {
			// No query param set - use max KW (first option when sorted newest first)
			const maxKw = sortOptsNewestFirst(opts)[0]?.value;
			if (maxKw !== undefined) {
				onChange(Number(maxKw));
			}
		}
	}, [opts]);

	// Update query params when value changes
	useEffect(() => {
		if (value !== undefined && value !== null) {
			const urlParams = new URLSearchParams(location.search);
			urlParams.set(queryParam, value.toString());

			const newSearch = urlParams.toString();
			const newUrl = location.pathname + '?' + newSearch;

			if (location.pathname + location.search !== newUrl) {
				history.replace(newUrl);
			}
		}
	}, [value, queryParam, location, history]);

	const handleChange = (name: string, opt: any) => {
		const newValue = parseInt(opt.value);
		onChange(newValue);
	};

	return (
		<div className={'grid is-col-auto align-center justify-start pad-1rem'}>
			<strong>KW: </strong>
			<div>
				<SelectInput name="weekPicker" emptyText={emptyText} value={value} onChange={handleChange} opts={sortOptsNewestFirst(opts)} isClearable={false} />
			</div>
		</div>
	);
});

export const WeekRangePicker = observer((props: RangePickerProps) => {
	const location = useLocation();
	const history = useHistory();
	const isInitializing = useRef(true);

	const { minValue, maxValue, opts, onMinChange, onMaxChange, emptyTextMin = 'Start KW', emptyTextMax = 'End KW', rangeLabel = 'KW', queryParam = 'kw' } = props;

	// Initialize from query params on mount - this runs FIRST
	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const minParam = urlParams.get(`${queryParam}Min`);
		const maxParam = urlParams.get(`${queryParam}Max`);

		// Always initialize from query params if they exist
		// This ensures query params take precedence on page refresh
		if (minParam) {
			const numValue = parseInt(minParam);
			if (!isNaN(numValue)) {
				onMinChange(numValue);
			}
		}

		if (maxParam) {
			const numValue = parseInt(maxParam);
			if (!isNaN(numValue)) {
				onMaxChange(numValue);
			}
		}

		// Mark initialization as complete after a small delay to allow store to settle
		setTimeout(() => {
			isInitializing.current = false;
		}, 100);
	}, []);

	// Update query params when values change (but not during initialization)
	useEffect(() => {
		// Skip URL updates during initial mount to avoid overwriting query params
		if (isInitializing.current) {
			return;
		}

		const urlParams = new URLSearchParams(location.search);

		if (minValue !== undefined && minValue !== null) {
			urlParams.set(`${queryParam}Min`, minValue.toString());
		} else {
			urlParams.delete(`${queryParam}Min`);
		}

		if (maxValue !== undefined && maxValue !== null) {
			urlParams.set(`${queryParam}Max`, maxValue.toString());
		} else {
			urlParams.delete(`${queryParam}Max`);
		}

		const newSearch = urlParams.toString();
		const newUrl = location.pathname + (newSearch ? '?' + newSearch : '');

		if (location.pathname + location.search !== newUrl) {
			history.replace(newUrl);
		}
	}, [minValue, maxValue, queryParam, location, history]);

	const handleMinChange = (name: string, opt: any) => {
		const newValue = parseInt(opt.value);
		onMinChange(newValue);
	};

	const handleMaxChange = (name: string, opt: any) => {
		const newValue = parseInt(opt.value);
		onMaxChange(newValue);
	};

	return (
		<div className={'grid is-col-auto align-center justify-start pad-1rem zeitraum-select'}>
			<strong>{rangeLabel}: </strong>
			<div>
				<SelectInput name="weekRangeMin" emptyText={emptyTextMin} value={minValue} onChange={handleMinChange} opts={sortOptsNewestFirst(opts)} isClearable={false} />
			</div>
			<span>bis</span>
			<div>
				<SelectInput name="weekRangeMax" emptyText={emptyTextMax} value={maxValue} onChange={handleMaxChange} opts={sortOptsNewestFirst(opts)} isClearable={false} />
			</div>
		</div>
	);
});
